/**
 * Hook for fetching target ranges from the API
 */
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { targetRangesApi, TargetRange } from '@/api/targetRanges';
import { useTankTargetRanges } from '@/api/measurements';
import { useTank } from '@/contexts/TankContext';
import { CACHE_TIMES } from '@/config/cacheTimes';

/**
 * Single target range item from tank-specific endpoint
 */
export interface TankTargetRange {
  parameterId: string;
  parameterName: string;
  unit: string;
  minValue: number | null;
  maxValue: number | null;
  enabled: boolean;
}

/**
 * Response type for tank-specific target ranges
 */
interface TankTargetRangesResponse {
  ranges: TankTargetRange[];
}

interface UseTargetRangesOptions {
  /** Whether to enable the query (defaults to true, combined with tankId check) */
  enabled?: boolean;
}

/**
 * Fetch the enabled target ranges for the current tank.
 *
 * Shares one query (and cache entry) with useTankTargetRanges, so every page
 * sees the same ranges. A failed fetch leaves `data` undefined with the error
 * on the query, rather than caching an empty list as if it had succeeded.
 */
export function useTargetRanges(options?: UseTargetRangesOptions): UseQueryResult<TankTargetRangesResponse> {
  const { selectedTank } = useTank();
  return useTankTargetRanges(selectedTank?.id, options);
}

/**
 * Fetch a single target range
 */
export function useTargetRange(paramKey: string): UseQueryResult<TargetRange> {
  return useQuery({
    queryKey: ['target-range', paramKey],
    queryFn: () => targetRangesApi.getTargetRange(paramKey),
    staleTime: CACHE_TIMES.CONFIG,
    retry: 2,
    enabled: !!paramKey,
  });
}

/**
 * Convert API response to Record format for easy lookup
 */
export function useTargetRangesMap(): {
  ranges: Record<string, TankTargetRange>;
  isLoading: boolean;
  error: Error | null;
} {
  const { data, isLoading, error } = useTargetRanges();

  const ranges: Record<string, TankTargetRange> = {};
  if (data?.ranges) {
    for (const range of data.ranges) {
      ranges[range.parameterId] = range;
    }
  }

  return { ranges, isLoading, error: error as Error | null };
}

/**
 * Range input for isWithinRange helper
 */
interface RangeInput {
  min_value?: number | null;
  max_value?: number | null;
}

/**
 * Determine if a value is within the target range
 * @param value - The measured value to check
 * @param range - The target range to check against (or undefined if no range configured)
 * @returns 'in-range' | 'below' | 'above' | 'unknown'
 */
export const isWithinRange = (
  value: number | null,
  range: RangeInput | undefined
): 'in-range' | 'below' | 'above' | 'unknown' => {
  // No range configured or value is null
  if (!range || value === null) return 'unknown';

  // Check if below minimum
  if (range.min_value !== undefined && range.min_value !== null && value < range.min_value) return 'below';

  // Check if above maximum
  if (range.max_value !== undefined && range.max_value !== null && value > range.max_value) return 'above';

  // Within range
  return 'in-range';
};
